import React, { useMemo, useState } from 'react';
import { objectionCountLabel } from '../services/risResearchStream';
import type { ReviewAnnotation } from '../services/risResearchStream';
import {
  externalLinkUrl,
  resolveViewerSourceTarget,
  risReferenceLabel,
  type ViewerHighlightContext,
  type ViewerSourceTarget,
} from '../services/risViewerLinks';

/**
 * Der Quellen-Fußbereich des Gesprächs. Er steht über dem Eingabefeld und
 * führt die Quellen aller Antworten der Sitzung -- statt eines eigenen
 * Quellenblocks unter jeder Antwort. Wieder geöffnete Sitzungen bauen ihn aus
 * den gespeicherten Nachrichten auf; ein neuer Lauf ergänzt ihn, sobald seine
 * Antwort steht.
 */

export interface SourcesFooterMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: readonly unknown[];
  verifiedSources?: readonly unknown[];
  reviewAnnotation?: ReviewAnnotation;
  researchMetadata?: { reviewAnnotation?: ReviewAnnotation };
}

export interface ConversationSourceRow {
  key: string;
  label: string;
  found: boolean;
  /** Herkunft der Quelle: die wievielte Antwort des Gesprächs sie angeführt hat. */
  origin: string;
  target: ViewerSourceTarget;
  /** Frage und Antworttext dieser Antwort, für die Hervorhebung im Viewer. */
  highlight: ViewerHighlightContext;
}

export interface ConversationSourceGroup {
  kind: string;
  label: string;
  rows: ConversationSourceRow[];
}

const GROUP_ORDER: ReadonlyArray<{ readonly kind: string; readonly label: string }> = [
  { kind: 'legislation', label: 'Gesetze' },
  { kind: 'rechtssatz', label: 'Rechtssätze' },
  { kind: 'decision', label: 'Entscheidungen' },
  { kind: 'collective_agreement', label: 'Kollektivverträge' },
  { kind: 'branchen_agb', label: 'Branchen-AGB' },
  { kind: 'web', label: 'Web' },
  { kind: 'other', label: 'Weitere Quellen' },
];

const KNOWN_KINDS = new Set(GROUP_ORDER.map(group => group.kind));

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function text(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

/** Ein Datum der Recherche steht als ISO-Tag; gezeigt wird die gewohnte Schreibweise. */
function readableDate(value: string): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return iso ? `${iso[3]}.${iso[2]}.${iso[1]}` : value;
}

function groupKindOf(source: unknown): string {
  const kind = text(asRecord(source).kind);
  return KNOWN_KINDS.has(kind) && kind !== 'other' ? kind : 'other';
}

/** Beschriftung aus dem Ziel, wenn die Quelle selbst keine Bezeichnung trägt. */
function targetLabel(target: ViewerSourceTarget): string {
  if (target.kind === 'ris') return risReferenceLabel(target.url);
  if (target.kind === 'external') {
    try {
      return new URL(target.url).hostname;
    } catch {
      return 'Externe Quelle';
    }
  }
  if (target.kind === 'document') return 'Dokument';
  return 'Quelle';
}

function rowLabel(source: unknown, target: ViewerSourceTarget): string {
  const record = asRecord(source);
  const base = text(record.reference) || text(record.title) || targetLabel(target);
  const decisionDate = text(record.decisionDate);
  if (decisionDate) return `${base} vom ${readableDate(decisionDate)}`;
  const validFrom = text(record.validFrom);
  if (validFrom) return `${base} gültig ab ${readableDate(validFrom)}`;
  return base;
}

/** Kennung, unter der dieselbe Quelle nur einmal im Verzeichnis steht. */
function identityOf(source: unknown, target: ViewerSourceTarget): string {
  if (target.kind === 'ris' || target.kind === 'external') return target.url;
  if (target.kind === 'document') return `document:${target.documentId}`;
  const record = asRecord(source);
  return `${text(record.reference)}\u0000${text(record.title)}`;
}

/**
 * Alle Quellen der Sitzung, nach Quellenart gruppiert und ohne Doppelnennung.
 * Geführt wird die geprüfte Liste einer Antwort; erst wenn sie fehlt, treten
 * die angeführten Quellen an ihre Stelle.
 */
export function aggregateConversationSources(
  messages: readonly SourcesFooterMessage[],
): ConversationSourceGroup[] {
  const rowsByKind = new Map<string, ConversationSourceRow[]>();
  const seen = new Set<string>();
  let answerIndex = 0;
  let question = '';

  for (const message of messages) {
    if (message.role === 'user') {
      question = message.content || '';
      continue;
    }
    answerIndex += 1;
    const answerNumber = answerIndex;
    const highlight: ViewerHighlightContext = { query: question, answer: message.content || '' };
    const verified = message.verifiedSources || [];
    const listed = verified.length > 0 ? verified : (message.sources || []);

    for (const source of listed) {
      const target = resolveViewerSourceTarget(source);
      const identity = identityOf(source, target);
      if (!identity.replace(/\u0000/g, '')) continue;
      if (seen.has(identity)) continue;
      seen.add(identity);

      const kind = groupKindOf(source);
      const rows = rowsByKind.get(kind) || [];
      rows.push({
        key: identity,
        label: rowLabel(source, target),
        found: asRecord(source).found !== false,
        origin: `Antwort ${answerNumber}`,
        target,
        highlight,
      });
      rowsByKind.set(kind, rows);
    }
  }

  return GROUP_ORDER.flatMap(group => {
    const rows = rowsByKind.get(group.kind);
    return rows && rows.length > 0 ? [{ kind: group.kind, label: group.label, rows }] : [];
  });
}

/**
 * Sammelabzeichen über alle geprüften Antworten des Gesprächs. Ohne eine
 * einzige Prüfung trägt der Kopf kein Abzeichen.
 */
export function conversationReviewBadge(messages: readonly SourcesFooterMessage[]): string | null {
  const reviews = messages.flatMap(message => {
    if (message.role !== 'assistant') return [];
    const review = message.reviewAnnotation || message.researchMetadata?.reviewAnnotation;
    return review ? [review] : [];
  });
  if (reviews.length === 0) return null;
  const objections = reviews.reduce(
    (total, review) => total + (review.status === 'objections' ? review.objections.length : 0),
    0,
  );
  if (objections > 0) return objectionCountLabel(objections);
  if (reviews.every(review => review.status === 'certified')) return 'Alle geprüft';
  return 'Prüfung teilweise nicht verfügbar';
}

function badgeClass(badge: string | null): string {
  if (badge === 'Alle geprüft') return 'border-emerald-300 bg-emerald-50 text-emerald-800';
  if (badge && /\d+ Einw(and|ände)$/.test(badge)) return 'border-amber-300 bg-amber-50 text-amber-900';
  return 'border-warm-200 bg-warm-100 text-muted';
}

const Tick: React.FC<{ found: boolean }> = ({ found }) => (
  <svg
    className={`mt-0.5 h-3 w-3 shrink-0 ${found ? 'text-emerald-700' : 'text-amber-700'}`}
    viewBox="0 0 12 12"
    aria-hidden="true"
  >
    {found ? (
      <path d="M2 6.5l2.5 2.5L10 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="M2.5 6h7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    )}
  </svg>
);

const ExternalMark: React.FC = () => (
  <span className="inline-flex items-center gap-1 text-[11px] text-muted dark:text-gray-400">
    <svg className="h-3 w-3" viewBox="0 0 12 12" aria-hidden="true">
      <path d="M4 8l4-4M4.5 4H8v3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    extern
  </span>
);

const ROW_LINK_CLASS = 'text-left text-primary underline decoration-warm-200 underline-offset-2 '
  + 'hover:text-accent-dark dark:text-gray-200 dark:decoration-gray-600';

export const SourcesFooter: React.FC<{
  sessionId: string | null;
  messages: readonly SourcesFooterMessage[];
  onOpenSource: (target: ViewerSourceTarget, highlight: ViewerHighlightContext) => void;
}> = ({ sessionId, messages, onOpenSource }) => {
  // Der Zustand gilt je Sitzung und lebt nur in der Oberfläche. Zugeklappt
  // beginnt der Bereich, damit die Antwort den Platz behält; der Kopf nennt die Zahl.
  const [expandedSessions, setExpandedSessions] = useState<Record<string, boolean>>({});
  const groups = useMemo(() => aggregateConversationSources(messages), [messages]);
  const badge = useMemo(() => conversationReviewBadge(messages), [messages]);

  const sessionKey = sessionId || 'ohne-sitzung';
  const expanded = expandedSessions[sessionKey] === true;
  const total = groups.reduce((count, group) => count + group.rows.length, 0);
  const bodyId = `quellen-${sessionKey.replace(/[^A-Za-z0-9_-]/g, '-')}`;

  if (total === 0) return null;

  return (
    <section className="mb-3 border border-warm-200 dark:border-gray-700" aria-label="Quellen dieses Gesprächs">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpandedSessions(previous => ({ ...previous, [sessionKey]: !expanded }))}
        className="flex w-full flex-wrap items-center gap-2 bg-warm-100 px-3 py-2 text-left dark:bg-gray-700"
      >
        <svg
          className={`h-4 w-4 shrink-0 text-muted transition-transform dark:text-gray-400 ${expanded ? 'rotate-90' : ''}`}
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-primary dark:text-gray-100">
          Quellen dieses Gesprächs
        </span>
        <span className="border border-warm-200 px-1.5 text-[11px] font-semibold text-muted dark:border-gray-600 dark:text-gray-300">
          {total}
        </span>
        {badge && (
          <span className={`inline-flex border px-2 py-0.5 text-[11px] font-semibold ${badgeClass(badge)}`}>
            {badge}
          </span>
        )}
        <span className="ml-auto text-[11px] text-muted dark:text-gray-400">
          RIS und Dokumente öffnen im Viewer, externe Seiten in einem neuen Tab
        </span>
      </button>

      <div
        id={bodyId}
        hidden={!expanded}
        className={`${expanded ? 'grid' : 'hidden'} max-h-[min(230px,30svh)] grid-cols-1 gap-x-6 gap-y-3 overflow-y-auto overscroll-contain px-3 py-3 min-[900px]:grid-cols-2`}
      >
        {groups.map(group => (
          <div key={group.kind}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted dark:text-gray-400">
              {group.label} <span className="font-normal">{group.rows.length}</span>
            </p>
            <ul className="mt-1.5 space-y-1 text-xs">
              {group.rows.map(row => {
                const href = row.target.kind === 'external' ? externalLinkUrl(row.target.url) : null;
                return (
                  <li key={row.key} className="flex items-start gap-2">
                    <Tick found={row.found} />
                    <span className="flex flex-wrap items-baseline gap-2">
                      {href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" className={ROW_LINK_CLASS}>
                          {row.label}
                        </a>
                      ) : row.target.kind === 'ris' || row.target.kind === 'document' ? (
                        <button
                          type="button"
                          onClick={() => onOpenSource(row.target, row.highlight)}
                          className={ROW_LINK_CLASS}
                        >
                          {row.label}
                        </button>
                      ) : (
                        <span className="text-primary dark:text-gray-200">{row.label}</span>
                      )}
                      {href && <ExternalMark />}
                      <span className="text-[11px] text-muted dark:text-gray-400">{row.origin}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

export default SourcesFooter;
